import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { ArrowLeft, Mail, Phone, MapPin, Calendar, GraduationCap, Users, ShieldAlert, Download, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

const EDITABLE_FIELDS = [
  // Personal
  ["name", "Full Name"], ["email", "Email"], ["phone", "Phone"], ["date_of_birth", "Date of Birth"],
  ["age", "Age"], ["gender", "Gender"], ["occupation", "Occupation"],
  ["address", "Address"], ["state", "State"], ["city", "City"],
  // Academic
  ["university", "University"], ["college", "College"], ["department", "Department"],
  ["honors", "Honors"], ["stream", "Stream / Specialisation"], ["interest", "Interest"],
  ["university_roll_number", "University Roll Number"], ["registration_number", "Registration Number"],
  // Family
  ["father_name", "Father's Name"], ["father_occupation", "Father's Occupation"],
  ["mother_name", "Mother's Name"], ["mother_occupation", "Mother's Occupation"],
  // Emergency
  ["emergency_contact_name", "Emergency Contact Name"], ["emergency_contact_relation", "Relation"],
  ["emergency_contact_phone", "Emergency Contact Phone"],
];

export default function AdminStudentDetail() {
  const { user_id } = useParams();
  const [user, setUser] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/users/${user_id}/detail`).then((r) => {
      setUser(r.data.user);
      setEnrollments(r.data.enrollments || []);
    }).catch(() => toast.error("Failed to load profile"));
  }, [user_id]);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/admin/users/${user_id}/profile-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(user?.name || "user").replace(/\s+/g, "_")}_profile.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to generate PDF");
    } finally { setDownloadingPdf(false); }
  };

  const startEdit = () => {
    const seed = {};
    EDITABLE_FIELDS.forEach(([k]) => { seed[k] = user?.[k] || ""; });
    setForm(seed);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      // Only send fields the admin actually edited / filled. Empty strings
      // are excluded so we don't accidentally blank out existing values.
      const payload = {};
      for (const [k] of EDITABLE_FIELDS) {
        const cur = (form[k] ?? "").toString().trim();
        const prev = (user?.[k] ?? "").toString().trim();
        if (cur !== prev) payload[k] = cur;
      }
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setEditing(false);
        return;
      }
      const { data } = await api.put(`/admin/users/${user_id}`, payload);
      // Merge to preserve any fields the backend may not have echoed back
      setUser((prev) => ({ ...(prev || {}), ...(data || {}) }));
      toast.success("Profile updated");
      const pn = data?.phone_notice;
      if (pn) {
        const parts = [pn.sms_sent ? "SMS sent" : "SMS not sent", pn.email_sent ? "email sent" : "email not sent"];
        (pn.sms_sent || pn.email_sent ? toast.success : toast.warning)(`Phone changed: ${parts.join(", ")}`);
      }
      setEditing(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
        : "Update failed";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (!user) {
    return <DashboardLayout><div className="p-10 text-center text-slate-400">Loading...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/admin/students" className="inline-flex items-center gap-1.5 text-sm text-brand font-semibold hover:underline" data-testid="back-to-students">
          <ArrowLeft size={14} /> Back to Students
        </Link>
        {!editing ? (
          <button onClick={startEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-semibold" data-testid="edit-student-button">
            <Pencil size={13} /> Edit Profile
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(false)} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-semibold" data-testid="cancel-edit-button">
              <X size={13} /> Cancel
            </button>
            <button onClick={saveEdit} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="save-student-button">
              <Save size={13} /> {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-5 mt-3">
        <div className="w-28 h-28 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
          {user.photo_url ? (
            <img src={ABS(user.photo_url)} alt={user.name} className="w-full h-full object-cover" data-testid="student-photo" />
          ) : (
            <div className="text-3xl font-black text-slate-300">{(user.name || "?").charAt(0).toUpperCase()}</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="heading text-2xl font-black">{user.name}</h1>
            <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-brand font-bold uppercase">{user.role}</span>
            {user.created_by_admin && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-50 text-amber-700 font-bold uppercase">Admin-created</span>}
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1.5 text-sm text-slate-600">
            <IconLine icon={Mail} v={user.email} />
            <IconLine icon={Phone} v={user.phone} />
            <IconLine icon={MapPin} v={[user.city, user.state].filter(Boolean).join(", ")} />
            <IconLine icon={Calendar} v={user.date_of_birth ? `DOB ${user.date_of_birth}` : ""} />
            <IconLine icon={GraduationCap} v={[user.college, user.department].filter(Boolean).join(" · ")} />
            <IconLine icon={Users} v={user.interest ? `Interest: ${user.interest}` : ""} />
          </div>
        </div>
      </div>

      {/* Info sections grid */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <InfoCard title="Personal Details">
          <Row editing={editing} form={form} setForm={setForm} k="name" label="Full Name" v={user.name} />
          <Row editing={editing} form={form} setForm={setForm} k="email" label="Email" v={user.email} />
          <Row editing={editing} form={form} setForm={setForm} k="phone" label="Phone" v={user.phone} />
          <Row editing={editing} form={form} setForm={setForm} k="date_of_birth" label="Date of Birth" v={user.date_of_birth} />
          <Row editing={editing} form={form} setForm={setForm} k="age" label="Age" v={user.age} />
          <Row editing={editing} form={form} setForm={setForm} k="gender" label="Gender" v={user.gender} />
          <Row editing={editing} form={form} setForm={setForm} k="occupation" label="Occupation" v={user.occupation} />
          <Row editing={editing} form={form} setForm={setForm} k="address" label="Address" v={user.address} />
          <Row editing={editing} form={form} setForm={setForm} k="state" label="State" v={user.state} />
          <Row editing={editing} form={form} setForm={setForm} k="city" label="City" v={user.city} />
        </InfoCard>

        <InfoCard title="Academic Details">
          <Row editing={editing} form={form} setForm={setForm} k="university" label="University" v={user.university} />
          <Row editing={editing} form={form} setForm={setForm} k="college" label="College" v={user.college} />
          <Row editing={editing} form={form} setForm={setForm} k="department" label="Department" v={user.department} />
          <Row editing={editing} form={form} setForm={setForm} k="honors" label="Honors" v={user.honors} />
          <Row editing={editing} form={form} setForm={setForm} k="stream" label="Stream / Specialisation" v={user.stream} />
          <Row editing={editing} form={form} setForm={setForm} k="interest" label="Interest" v={user.interest} />
          <Row editing={editing} form={form} setForm={setForm} k="university_roll_number" label="University Roll No" v={user.university_roll_number} />
          <Row editing={editing} form={form} setForm={setForm} k="registration_number" label="Registration No" v={user.registration_number} />
        </InfoCard>

        <InfoCard title="Family Details">
          <Row editing={editing} form={form} setForm={setForm} k="father_name" label="Father's Name" v={user.father_name} />
          <Row editing={editing} form={form} setForm={setForm} k="father_occupation" label="Father's Occupation" v={user.father_occupation} />
          <Row editing={editing} form={form} setForm={setForm} k="mother_name" label="Mother's Name" v={user.mother_name} />
          <Row editing={editing} form={form} setForm={setForm} k="mother_occupation" label="Mother's Occupation" v={user.mother_occupation} />
        </InfoCard>

        <InfoCard title="Emergency Contact" icon={ShieldAlert}>
          <Row editing={editing} form={form} setForm={setForm} k="emergency_contact_name" label="Contact Person" v={user.emergency_contact_name} />
          <Row editing={editing} form={form} setForm={setForm} k="emergency_contact_relation" label="Relation" v={user.emergency_contact_relation} />
          <Row editing={editing} form={form} setForm={setForm} k="emergency_contact_phone" label="Contact Number" v={user.emergency_contact_phone} />
        </InfoCard>
      </div>

      {enrollments.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="heading text-sm font-black uppercase tracking-[0.2em] text-brand mb-3">Test Series Enrollments ({enrollments.length})</h3>
          <div className="divide-y divide-slate-100">
            {enrollments.map((e) => (
              <div key={e.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{e.test_title}</div>
                  <div className="text-xs text-slate-500">{e.status} · {new Date(e.enrolled_at).toLocaleDateString()}</div>
                </div>
                {e.attempt_score !== undefined && e.attempt_score !== null && (
                  <div className="text-sm font-bold text-emerald-600">{e.attempt_score}/{e.attempt_total}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={downloadPdf}
          disabled={downloadingPdf}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand hover:bg-[#172b66] disabled:opacity-60 text-sm font-semibold text-white"
          data-testid="download-profile-pdf"
        >
          <Download size={14} /> {downloadingPdf ? "Generating PDF..." : "Download Profile (PDF)"}
        </button>
        <a
          href={`data:application/json,${encodeURIComponent(JSON.stringify(user, null, 2))}`}
          download={`${(user?.name || "user").replace(/\s+/g, "_")}_profile.json`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700"
          data-testid="download-profile-json"
        >
          <Download size={14} /> Export JSON
        </a>
      </div>
    </DashboardLayout>
  );
}

function IconLine({ icon: Icon, v }) {
  if (!v) return null;
  return <div className="flex items-center gap-1.5"><Icon size={14} className="text-slate-400" />{v}</div>;
}

function InfoCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} className="text-[#F59E0B]" />}
        <h3 className="heading text-sm font-black uppercase tracking-[0.2em] text-brand">{title}</h3>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function Row({ editing, form, setForm, k, label, v }) {
  return (
    <div className="py-2 grid grid-cols-[140px_1fr] gap-3 text-sm items-center">
      <div className="text-slate-500 font-semibold">{label}</div>
      {editing ? (
        <input
          value={form[k] ?? ""}
          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          className="px-2 py-1 rounded border border-slate-300 focus:border-amber-500 outline-none text-sm"
          data-testid={`edit-${k}`}
        />
      ) : (
        <div className="text-slate-800">{v || <span className="text-slate-300">—</span>}</div>
      )}
    </div>
  );
}
